import {
	type Action,
	configureStore,
	type ThunkDispatch,
} from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import * as Sentry from '@sentry/react'
import undoable, { includeAction } from 'redux-undo'
import { graphqlApi } from 'src/redux/api/graphql/graphqlApi'
import { restApi } from 'src/redux/api/rest/restApi'
import { storageApi } from 'src/redux/api/rest/storageApi'
import authReducer from '../slices/auth'
import bankingReducer from '../slices/banking'
import chatReducer from '../slices/chat'
import clientsReducer from '../slices/clients'
import commonReducer from '../slices/common'
import contactsReducer from '../slices/contacts'
import dashboardReducer from '../slices/dashboard'
import invoicesReducer from '../slices/invoices'
import projectsReducer from '../slices/projects'
import proposalsReducer from '../slices/proposals'
import purchaseordersReducer from '../slices/purchaseOrders'
import purchasesReducer from '../slices/purchases'
import quotesReducer from '../slices/quotes'
import reportingReducer from '../slices/reporting'
import settingsReducer from '../slices/settings'
import suppliersReducer from '../slices/suppliers'

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
	actionTransformer: (action: any) => {
		if (
			action.type === 'auth/signUpFreelanceUser' ||
			action.type === 'auth/signUpCompanyAdminUser' ||
			action.type === 'auth/loginUser' ||
			action.type === 'auth/confirmCode'
		) {
			// Return null to not log the action to Sentry
			return null
		}
		return action
	},
})
// test
const store = configureStore({
	reducer: {
		auth: authReducer,
		banking: bankingReducer,
		clients: clientsReducer,
		projects: projectsReducer,
		// @ts-expect-error
		quotes: undoable(quotesReducer, {
			limit: 10, // set a limit for the size of the history
			filter: includeAction('quote/setQuoteInfo'),
			ignoreInitialState: true, // prevent user from undoing to the beginning, ex: client-side hydration
		}),
		settings: settingsReducer,
		common: commonReducer,
		contacts: contactsReducer,
		proposals: proposalsReducer,
		invoices: invoicesReducer,
		suppliers: suppliersReducer,
		purchases: purchasesReducer,
		purchaseorders: purchaseordersReducer,
		dashboard: dashboardReducer,
		reporting: reportingReducer,
		chat: chatReducer,
		[graphqlApi.reducerPath]: graphqlApi.reducer,
		[restApi.reducerPath]: restApi.reducer,
		[storageApi.reducerPath]: storageApi.reducer,
	},
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			immutableCheck: false,
			serializableCheck: false,
		})
			.concat(graphqlApi.middleware)
			.concat(restApi.middleware)
			.concat(storageApi.middleware),
	enhancers: [sentryReduxEnhancer],
})

setupListeners(store.dispatch)

export default store

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export type ThunkAppDispatch = ThunkDispatch<RootState, void, Action>
